/**
 * Namespaced logging for debugging. Debug/info are dev-only unless VITE_DEBUG_LOGS=1.
 * Warnings always go to the console.
 * Errors go to both console AND Sentry (production-safe: Sentry helper never throws).
 */
import * as Sentry from '@sentry/react'

const debugEnabled =
  import.meta.env.DEV || import.meta.env.VITE_DEBUG_LOGS === '1'

function sentryCapture(scope: string, args: unknown[]): void {
  try {
    const client = Sentry.getClient()
    if (!client) return
    // First arg that is an Error goes to captureException, otherwise captureMessage
    const err = args.find((a) => a instanceof Error)
    if (err instanceof Error) {
      Sentry.withScope((s) => {
        s.setTag('logger_scope', scope)
        Sentry.captureException(err)
      })
    } else {
      const msg = args.map((a) => (typeof a === 'string' ? a : JSON.stringify(a))).join(' ')
      Sentry.withScope((s) => {
        s.setTag('logger_scope', scope)
        s.setLevel('error')
        Sentry.captureMessage(`[${scope}] ${msg}`)
      })
    }
  } catch {
    // Never let Sentry crash the app
  }
}

export function createLogger(scope: string) {
  const prefix = `[${scope}]`
  return {
    debug: (...args: unknown[]) => {
      if (debugEnabled) console.debug(prefix, ...args)
    },
    info: (...args: unknown[]) => {
      if (debugEnabled) console.info(prefix, ...args)
    },
    warn: (...args: unknown[]) => console.warn(prefix, ...args),
    error: (...args: unknown[]) => {
      console.error(prefix, ...args)
      sentryCapture(scope, args)
    },
  }
}
